import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import _ from "lodash";
import moment from "moment/moment";

import {
  extendAssetsWithDataAttribute,
  getRiskScoreConfirmation,
  PaymentPlanEditInstrumentsHandler,
  PayoutPlanAddInstrumentsHandler,
  SavingPlanAddInstrumentsHandler,
  SwitchInPlanInstrumentsHandler,
} from "../../../../../../RiskProfiling/utils";
import {
  buildModelPortfolioTableData,
  checkSavingsPlanChanged,
  getTransactionItemSRRI,
  instrCustodianData,
  setEarliestPriceDates,
  setInstrumentBankData,
} from "../../../../../utils";
import {
  nextAvailableDate,
  SERVICE_CONCEPTS,
  TRANSACTION_INSTR,
  TRANSACTION_TYPE_VALUES,
} from "../../../../../constants";
import { setNextExecutionDateIfNeeded } from "./usePaymentPlanTrade";
import {
  TRADING_ACTION_PAYOUT_PLAN,
  TRADING_ACTION_SAVINGS_PLAN,
  TRADING_ACTION_SWITCH_PLAN,
} from "../../../../../../../components/Charts/InstrumentsAllocationTable/constants";
import {
  getAssetInternalId,
  hasUnusedPaymentPlans,
  paymentPlanHasPosition,
  portfolioIsPaymentPlansOnly,
} from "../../../../../../CustomerDashboard/utils";
import { PAYMENT_PLANS_PORTFOLIO_ONLY_DELETE_PAYMENT_PLAN_MSG } from "../../../../../../RiskProfiling/constants";
import { getPortfolioInstrumentsData } from "../../../CombinedTradeStepV2";
import { fetchAssetPrice } from "../../../../TradeStep/components/PortfolioTrade/PortfolioTrade";
import { useConfirmationModalContext } from "../../../../../../../components/ConfirmationModalContextProvider";
import {
  addCombinedPaymentPlanTradingOption,
  removeCombinedTradingOption,
  removePortfolioCombinedTradingOption,
} from "../../../../../../../components/TradingStore/actions";

const isSameAsset = (a, asset) => {
  if (asset.action === "create") {
    return a.action === "create" && a._id === asset._id;
  }
  return a.data.payment_id === asset.data.payment_id;
}

export default function usePaymentPlanEdit({
  portfolio,
  allInstruments,
  onInstrumentsChange,
  customerId,
  action,
  groupInstruments,
  defaults,
  updateDefaults,
  dataService,
  transactionType,
  isVirtual,
}) {
  const dispatch = useDispatch();
  const modalContext = useConfirmationModalContext();
  const idCounter = useRef(allInstruments.length);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmRiskModalState, setConfirmRiskModalState] = useState({
    open: false,
    activeAsset: undefined,
  });

  const tradingType = useMemo(
    () =>
      ({
        [TRANSACTION_TYPE_VALUES.SWITCH_PLAN]: TRADING_ACTION_SWITCH_PLAN,
        [TRANSACTION_TYPE_VALUES.PAYOUT_PLAN]: TRADING_ACTION_PAYOUT_PLAN,
        [TRANSACTION_TYPE_VALUES.SAVINGS_PLAN]: TRADING_ACTION_SAVINGS_PLAN,
      }[transactionType]),
    [transactionType]
  );

  const isAnlageberatung =
    dataService.serviceConcept === SERVICE_CONCEPTS.Anlageberatung;

  const instrumentsHandlers = useMemo(() => {
    if (isVirtual) return {};

    let disableFunds;
    if (portfolio.depotType) {
      disableFunds = portfolio.depotType.configuration.trading_funds_disabled;
    }

    switch (transactionType) {
      case TRANSACTION_TYPE_VALUES.SWITCH_PLAN:
        return {
          create: new SwitchInPlanInstrumentsHandler(
            portfolio.companyId, undefined, dataService.targetMarket, undefined, disableFunds
          ),
          edit: new PaymentPlanEditInstrumentsHandler(portfolio.companyId, disableFunds),
        };
      case TRANSACTION_TYPE_VALUES.PAYOUT_PLAN:
        return {
          create: new PayoutPlanAddInstrumentsHandler(
            portfolio.companyId, undefined, undefined, undefined, disableFunds
          ),
        };
      case TRANSACTION_TYPE_VALUES.SAVINGS_PLAN:
        return {
          create: new SavingPlanAddInstrumentsHandler(
            portfolio.companyId, undefined, dataService.targetMarket, undefined, disableFunds
          ),
          edit: new PaymentPlanEditInstrumentsHandler(portfolio.companyId, disableFunds),
        };
      default:
        return {};
    }
  }, [isVirtual, portfolio.depotType, portfolio.companyId, dataService.targetMarket, transactionType]);

  useEffect(() => {
    // validate on depotType change
    if (!portfolio.isModelportfolio) {
      (async () => {
        if (!_.isEmpty(groupInstruments)) {
          const itemAction = _.get(groupInstruments, "0.action");
          const handler = instrumentsHandlers[itemAction];
          if (handler) {
            const instruments = getPortfolioInstrumentsData(groupInstruments, true);
            await handler.getCustodianAndFilterInstruments(instruments, true);
          }
        }
      })();
    }
  }, [portfolio.depotType])

  const handleChange = useCallback(
    (newAsset, data) => {
      const item = {...newAsset, errors: {...newAsset.errors}};

      Object.keys(data).forEach((key) => { item[key] = data[key]; });
      _.unset(item, "errors.no_changes");
      checkSavingsPlanChanged(item);

      const preventDateUpdate = isVirtual && item.action === "edit";
      if (!preventDateUpdate && item.is_changed && !item.changes.from_date) {
        const nextDate = nextAvailableDate(item.from_date, portfolio, transactionType);
        if (nextDate) {
          item.from_date = nextDate;
        }
      }

      onInstrumentsChange(allInstruments.map(a => isSameAsset(a, item) ? item : a));
    },
    [allInstruments, isVirtual, portfolio, transactionType, onInstrumentsChange]
  );

  // Side effect: fetch prices for virtual edit items that don't have price_eur yet
  useEffect(() => {
    if (!isVirtual) return;

    let hasChanges = false;
    const updated = allInstruments.map((a) => {
      const item = groupInstruments.find((i) => isSameAsset(i, a));
      if (!item) return a;

      if (!item.loading && item.action === "edit" && item.from_date && !_.has(item, "data.price_eur")) {
        hasChanges = true;
        const updatedItem = { ...item, from_date: moment(item.from_date) };
        _.unset(updatedItem, "errors.no_changes");
        checkSavingsPlanChanged(updatedItem);
        return updatedItem;
      }
      return a;
    });

    if (hasChanges) {
      onInstrumentsChange(updated);
    }
  }, [groupInstruments]);

  const handleAmountChange = useCallback(
    (item, value) => {
      handleChange(item, {
        transaction_value: value,
        errors: { ...item.errors, transaction_value: null },
      });
    },
    [handleChange]
  );

  const handleDiscountChange = useCallback(
    (item, value) => {
      handleChange(item, {
        discount: value,
        errors: { ...item.errors, discount: null },
      });
      setDefaultsForNew(item.action, { discount: value });
    },
    [handleChange]
  );

  const setDefaultsForNew = useCallback(
    (itemAction, changes) => {
      if (itemAction === "create") {
        updateDefaults(changes);
      }
    },
    [updateDefaults]
  );

  const handlePlanDataChange = useCallback((item, data, populate=false) => {
    handleChange(item, {
      ...data,
      errors: { ...item.errors, rotation: null, from_date: null, till_date: null },
    });

    setDefaultsForNew(item.action, {
      rotation: data.rotation,
      from_date: data.from_date,
      till_date: data.till_date,
    });
  }, [handleChange, setDefaultsForNew])

  // TODO: refactor
  const handleAddItems = useCallback(
    async (targetAction, selectedAssets, switchOutItem) => {
      const instrumentsCopy = [...allInstruments];
      const isSwitch = !!switchOutItem;

      if (isSwitch) {
        _.unset(switchOutItem, "errors.buy");
      }

      const transactionTypeValid =
        (transactionType === TRANSACTION_TYPE_VALUES.SWITCH_PLAN && isSwitch) ||
        transactionType === TRANSACTION_TYPE_VALUES.SAVINGS_PLAN;

      if (targetAction === "delete") {
        const portfolioComponents = portfolio.data.hasOwnProperty("base_components")
          ? portfolio.data.base_components
          : portfolio.data.components;
        const portfolioSavingsPlans = portfolio.data.hasOwnProperty("savings_plan")
          ? portfolio.data.savings_plan
          : portfolio.data.components;

        const deleteConfirmationRequired =
          transactionType === TRANSACTION_TYPE_VALUES.SAVINGS_PLAN &&
          isAnlageberatung &&
          portfolioIsPaymentPlansOnly(portfolioComponents, portfolioSavingsPlans) &&
          _.some(selectedAssets.map((asset) => paymentPlanHasPosition(asset, portfolioComponents))) &&
          _.some(
            selectedAssets.map(
              (asset) =>
                !hasUnusedPaymentPlans(
                  asset.isin,
                  asset.payment_id,
                  portfolioSavingsPlans,
                  portfolio.transactions.savings_plan || []
                )
            )
          );

        if (
          deleteConfirmationRequired &&
          !(await modalContext.confirm(PAYMENT_PLANS_PORTFOLIO_ONLY_DELETE_PAYMENT_PLAN_MSG, "Bitte bestätigen Sie hier"))
        ) {
          return false;
        }
      } else if (targetAction === "create") {
        const riskScoreConfirmation =
          transactionTypeValid &&
          getRiskScoreConfirmation(dataService, extendAssetsWithDataAttribute(selectedAssets), false, getTransactionItemSRRI);

        if (riskScoreConfirmation && !(await modalContext.confirm(riskScoreConfirmation, "Bitte bestätigen Sie hier"))) {
          return false;
        }
      }

      const findSwitchInIndex = (assets, asset) =>
        _.findIndex(assets, (a) => getAssetInternalId(a.data) === getAssetInternalId(asset));

      selectedAssets.forEach((item) => {
        if (!isSwitch || findSwitchInIndex([switchOutItem, ...switchOutItem.buy], item) === -1) {
          const instrument = {
            data: item,
            action: targetAction,
            transaction_type: TRANSACTION_INSTR.amount,
            ...(targetAction === "create" && defaults || {}),
          };

          if (targetAction === "create") {
            instrument._id = ++idCounter.current;
          }

          if (!isVirtual) {
            setNextExecutionDateIfNeeded(instrument, portfolio, transactionType);
          }
          setInstrumentBankData(instrument, instrCustodianData(item, portfolio.companyId), true);

          if (isVirtual && targetAction === "edit") {
            _.unset(instrument, "data.price_eur");
          }

          if (!isSwitch) {
            if (transactionType === TRANSACTION_TYPE_VALUES.SWITCH_PLAN) {
              instrument.tradingType = TRADING_ACTION_SWITCH_PLAN;
            }
            instrumentsCopy.push(instrument);

            if (isVirtual) {
              instrument.tradingType = tradingType;
            } else {
              dispatch(addCombinedPaymentPlanTradingOption({ customer_id: customerId }, portfolio.data, _.cloneDeep(instrument.data), targetAction, tradingType));
            }
          } else {
            switchOutItem.buy.push(instrument);
          }
        }
      });

      if (instrumentsCopy.length && isVirtual) {
        setLoading(true);
        await setEarliestPriceDates(instrumentsCopy, dispatch);
        setLoading(false);
      }

      onInstrumentsChange(instrumentsCopy);
      setDialogOpen(false);
    },
    [
      allInstruments, transactionType, dataService, portfolio, isVirtual,
      defaults, tradingType, customerId, dispatch, modalContext,
      onInstrumentsChange, isAnlageberatung
    ]
  );

  const dispatchRemoval = useCallback(
    (item) => {
      if (portfolio.isModelportfolio) {
        dispatch(removePortfolioCombinedTradingOption(customerId, portfolio.portfolioId));
      } else {
        dispatch(removeCombinedTradingOption(customerId, portfolio.portfolioId, getAssetInternalId(item.data)));
      }
    },
    [portfolio, customerId, dispatch]
  );

  const onRemoveClick = useCallback(
    (item, switchOutItem = undefined) => {
      if (switchOutItem) {
        handleChange(switchOutItem, {
          buy: switchOutItem.buy.filter((i) => !isSameAsset(i, item)),
        });
        return;
      }

      const newInstruments = allInstruments.filter((i) => !isSameAsset(i, item));
      dispatchRemoval(item);
      onInstrumentsChange(newInstruments);
    },
    [allInstruments, onInstrumentsChange, handleChange, dispatchRemoval]
  );

  const onRemoveAllClick = useCallback(
    (switchOutItem = undefined) => {
      if (switchOutItem) {
        handleChange(switchOutItem, { buy: [] });
        return;
      }

      const instrumentsToKeep = allInstruments.filter((i) => {
        if (i.action === action) {
          dispatchRemoval(i);
          return false;
        }
        return true;
      });
      onInstrumentsChange(instrumentsToKeep);
    },
    [allInstruments, action, onInstrumentsChange, handleChange, dispatchRemoval]
  );

  const handleAddSavingPlanButtonClick = useCallback(
    async (targetAction) => {
      if (!portfolio.isModelportfolio || ["delete", "edit"].includes(targetAction)) {
        setDialogOpen(true);
        return;
      }

      const portfolioPrepared = _.cloneDeep(portfolio);
      portfolioPrepared.srri = getTransactionItemSRRI(portfolio);
      const riskScoreConfirmation =
        transactionType !== TRANSACTION_TYPE_VALUES.PAYOUT_PLAN &&
        getRiskScoreConfirmation(dataService, extendAssetsWithDataAttribute([portfolioPrepared]), false, getTransactionItemSRRI);

      if (riskScoreConfirmation && !(await modalContext.confirm(riskScoreConfirmation, "Bitte bestätigen Sie hier"))) {
        return false;
      }

      const instrument = {
        data: buildModelPortfolioTableData(portfolio),
        action: targetAction,
        transaction_type: TRANSACTION_INSTR.amount,
        broker_fee: portfolio.broker_fee,
        ...defaults,
      };

      if (targetAction === "create") {
        instrument._id = ++idCounter.current;
      }

      dispatch(addCombinedPaymentPlanTradingOption({ customer_id: customerId }, portfolio.data, instrument.data, targetAction, tradingType));
      onInstrumentsChange([...allInstruments, instrument]);
    },
    [portfolio, allInstruments, transactionType, dataService, defaults, customerId, tradingType, dispatch, modalContext, onInstrumentsChange]
  );

  const handleConfirmRiskModalSave = useCallback(
    (item, value) => {
      handleChange(item, {
        risk_score_explanation: value,
        errors: { ...item.errors, risk_score_explanation: null },
      });
      setConfirmRiskModalState({ open: false, activeAsset: undefined });
    },
    [handleChange]
  );

  const handleSelectAsset = useCallback((asset) => {
    setConfirmRiskModalState({ open: true, activeAsset: asset });
  }, []);

  return {
    dialogOpen,
    setDialogOpen,
    loading,
    instrumentsHandlers,
    confirmRiskModalState,
    isAnlageberatung,
    tradingType,
    handleChange,
    handleAmountChange,
    handleDiscountChange,
    handlePlanDataChange,
    handleAddItems,
    handleAddSavingPlanButtonClick,
    onRemoveClick,
    onRemoveAllClick,
    handleConfirmRiskModalSave,
    handleSelectAsset,
  };
}
